export type DocumentType =
  | 'COPARN'
  | 'COARRI'
  | 'IFTSTA'
  | 'IFTMAN'
  | 'CODECO'
  | 'CODECO_GIN'
  | 'CODECO_GOU'
  | 'CUSCAR'
  | 'COREOR'
  | 'CUSRES'
  | 'DUM'
  | 'AMPI'
  | 'AMI'
  | 'AMPE'
  | 'PESAGE'
  | 'RESULTAT_SCANNER'
  | 'IMAGE_SCANNER'
  | 'TRYPTIQUE'
  | 'ACK_PS'
  | 'ACK_CECOR'
  | 'ACK_CTCA'
  | 'CUSTOM_JSON'
  | 'CUSTOM_XML';

export const ALL_DOCUMENT_TYPE_OPTIONS = [
  'COPARN',
  'COARRI',
  'IFTSTA',
  'IFTMAN',
  'CODECO',
  'CODECO_GIN',
  'CODECO_GOU',
  'CUSCAR',
  'COREOR',
  'CUSRES',
  'DUM',
  'AMPI',
  'AMI',
  'AMPE',
  'CUSTOM_JSON',
  'CUSTOM_XML',
] as const satisfies readonly DocumentType[];

export const MARITIME_DOCUMENT_TYPE_OPTIONS = [
  'COPARN',
  'COARRI',
  'IFTSTA',
  'IFTMAN',
  'CODECO_GIN',
  'CODECO_GOU',
  'CUSCAR',
  'COREOR',
  'CUSRES',
  'DUM',
  'AMPI',
  'AMI',
  'AMPE',
] as const satisfies readonly DocumentType[];

/**
 * Every type that can appear in the Documents list: the generatable maritime types plus the
 * outbound messages other modules mirror there (Pesage, scanner, Tryptique, and the functional
 * ACKs NWM sends back to ADII). Filter-only -- these extra types are never offered for generation.
 */
export const DOCUMENT_LIST_TYPE_OPTIONS = [
  ...MARITIME_DOCUMENT_TYPE_OPTIONS,
  'PESAGE',
  'RESULTAT_SCANNER',
  'IMAGE_SCANNER',
  'TRYPTIQUE',
  'ACK_PS',
  'ACK_CECOR',
  'ACK_CTCA',
] as const satisfies readonly DocumentType[];

export type DocumentStatus =
  | 'CREATED'
  | 'VALID'
  | 'INVALID'
  | 'READY'
  | 'RESENT'
  | 'REBUILT'
  | 'PENDING';

export interface ErrorDetail {
  field: string;
  issue: string;
}

export interface ApiErrorResponse {
  code: string;
  message: string;
  details: ErrorDetail[];
  traceId: string;
}

export interface ApiListResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  nextCursor?: string;
}

export interface ApiItemResponse<T> {
  data: T;
}

export interface DocumentValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  validatedAt?: string;
}

export interface DocumentSummary {
  id: string;
  documentType: DocumentType;
  status: DocumentStatus;
  partnerId?: string;
  filename?: string;
  currentVersionNumber: number;
  latestDelivery?: {
    status: string;
    transport?: string;
    mode?: string;
    destination?: string;
    message?: string;
    reason?: string;
    attemptedAt?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface DocumentDetails extends DocumentSummary {
  referenceId?: string;
  correlationId?: string;
  mappingId?: string;
  templateId?: string;
  sourcePayloadId: string;
  sourcePayload: Record<string, unknown>;
  format?: 'EDI' | 'XML';
  filename?: string;
  contentType?: string;
  body?: string;
  metadata: Record<string, unknown>;
  validationErrors: string[];
  validationResult: DocumentValidationResult;
  attempts: Array<{
    id: string;
    attemptType: 'GENERATE' | 'VALIDATE' | 'RESEND' | 'REBUILD';
    status: 'SUCCESS' | 'FAILED';
    documentVersionId?: string;
    documentVersionNumber?: number;
    reason?: string;
    details: Record<string, unknown>;
    traceId?: string;
    createdAt: string;
  }>;
}

export interface GenerateDocumentRequest {
  documentType: DocumentType;
  partnerId: string;
  referenceId: string;
  correlationId: string;
  payload: Record<string, unknown>;
}

export interface CreateRawDocumentRequest {
  documentType: DocumentType;
  partnerId: string;
  referenceId: string;
  correlationId: string;
  filename: string;
  contentType: string;
  body: string;
}

export interface GeneratedDocumentResponse {
  id: string;
  format: 'EDI' | 'XML';
  documentType: DocumentType;
  filename: string;
  contentType: string;
  body: string;
  partnerId: string;
  correlationId: string;
  status: 'CREATED' | 'VALID' | 'INVALID' | 'READY' | 'RESENT' | 'REBUILT';
  validation: {
    isValid: boolean;
    errors: string[];
    warnings: string[];
  };
  metadata: Record<string, unknown>;
}

export interface DocumentListQuery {
  page?: number;
  pageSize?: number;
  cursor?: string;
  sortBy?: 'createdAt' | 'updatedAt' | 'documentType' | 'status' | 'partnerId';
  sortOrder?: 'asc' | 'desc';
  documentType?: DocumentType | '';
  partnerId?: string;
  status?: DocumentStatus | '';
  createdFrom?: string;
  createdTo?: string;
  updatedFrom?: string;
  updatedTo?: string;
  correlationId?: string;
  traceId?: string;
  referenceId?: string;
  filename?: string;
}

export type As2InboundProcessingStatus = 'PERSISTED' | 'PROCESSED' | 'FAILED';

export interface InboundDumDetail {
  id: string;
  inboundDocumentId: string;
  correlationId: string;
  as2MessageId: string;
  partnerCode: string;
  rootElement: string;
  flow: string;
  referenceDUM: string;
  referenceMessage: string;
  messageFonction: string;
  adresseEdi: string;
  resultatCtrl?: string;
  parsedPayload: Record<string, unknown>;
  createdAt: string;
}

export interface CusdecMlValidationIssue {
  code: string;
  field: string;
  message: string;
}

export interface CusdecMlDetail {
  id: string;
  inboundDocumentId: string | null;
  correlationId: string;
  messageType: string;
  operation: string;
  rootElement: string;
  filename: string | null;
  partnerCode: string | null;
  referenceDum: string | null;
  referenceMessage: string | null;
  referenceDumInitiale: string | null;
  messageFunctionCode: string | null;
  messageFunctionLabel: string | null;
  versionDum: number | null;
  dateMlRaw: string | null;
  dateMlTimestamp: string | null;
  avecPesageMl: string | null;
  avecScannerMl: string | null;
  valeurDeclare: string | null;
  codeDeclarant: string | null;
  codeSoumissionnaire: string | null;
  validationStatus: string;
  processingStatus: string;
  processingError: string | null;
  normalizedJson: Record<string, unknown> | null;
  validationReport: {
    status: string;
    errors: CusdecMlValidationIssue[];
    warnings: CusdecMlValidationIssue[];
    xsdErrors: CusdecMlValidationIssue[];
  } | null;
  timeline: Array<{ at: string; status: string; detail?: string }>;
  createdAt: string;
  updatedAt: string;
}

export interface InboundAs2DocumentSummary {
  id: string;
  correlationId: string;
  partnerCode: string;
  as2MessageId: string;
  documentType: string;
  /** The real message family, re-resolved live from the stored payload/filename (root element,
   * BGM, etc.) -- documentType alone is only the AS2 Subject-derived hint, frequently "UNKNOWN"
   * since ADII rarely sets a meaningful Subject header. Prefer this for display. */
  routingTarget?: string;
  contentType: string;
  receivedAt: string;
  processingStatus: As2InboundProcessingStatus;
  processingError?: string;
  createdAt: string;
  updatedAt: string;
}

export interface InboundRoutingDecision {
  target: string;
  ruleDescription: string;
}

export interface InboundAs2DocumentDetails extends InboundAs2DocumentSummary {
  payload: string;
  security: Record<string, unknown>;
  routingDecision: InboundRoutingDecision;
  dumDetail?: InboundDumDetail;
  cusdecMlDetail?: CusdecMlDetail;
}

export interface InboundDocumentListQuery {
  page?: number;
  pageSize?: number;
  sortBy?: 'receivedAt' | 'partnerCode' | 'documentType' | 'processingStatus';
  sortOrder?: 'asc' | 'desc';
  partnerCode?: string;
  documentType?: string;
  processingStatus?: As2InboundProcessingStatus | '';
  as2MessageId?: string;
  correlationId?: string;
  receivedFrom?: string;
  receivedTo?: string;
}

export interface ResourceRecord {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export interface PartnerRecord extends ResourceRecord {
  code: string;
  protocol: 'EDI' | 'XML';
  isActive: boolean;
  settings: Record<string, unknown>;
  organizationId?: string;
  legalName?: string;
  taxIdentifiers?: Record<string, unknown>;
  contacts?: Record<string, unknown>[];
  operationalStatus?: 'ONBOARDING' | 'OPERATIONAL' | 'PAUSED' | 'OFFBOARDED';
  supportedProtocols?: string[];
  inboundCapabilities?: string[];
  outboundCapabilities?: string[];
  supportedMessageTypes?: string[];
  throughputLimits?: Record<string, unknown>;
  slaProfile?: Record<string, unknown>;
  securityProfile?: Record<string, unknown>;
  environmentProfile?: 'DEV' | 'TEST' | 'UAT' | 'PROD';
  lifecycleStatus?: 'DRAFT' | 'PENDING_APPROVAL' | 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';
  version?: number;
  runtimeProfile?: PartnerRuntimeProfileRecord;
  activeRuntimeProfile?: PartnerRuntimeProfileRecord;
  expectedVersion?: number;
  lastConnectivityStatus?: 'UNKNOWN' | 'PASSED' | 'FAILED' | 'SKIPPED';
  lastConnectivityCheckedAt?: string;
}

export type PartnerAuthenticationType = 'NONE' | 'BASIC' | 'BEARER' | 'API_KEY' | 'OAUTH2' | 'AS2' | 'JWT_LOGIN';

export interface PartnerDestinationMetadata {
  code: string;
  label: string;
  runtimeWired: boolean;
  selectable: boolean;
  supportedMessageFamilies: string[];
  disabledReason?: string;
}

export interface PartnerAuthTypeMetadata {
  code: PartnerAuthenticationType;
  label: string;
  selectable: boolean;
  implemented: boolean;
  supportedMessageFamilies: string[];
  requiredSecretReferences: string[];
  requiredFields: string[];
  disabledReason?: string;
}

export interface PartnerSecretProviderMetadata {
  code: 'VAULT' | 'ENV' | 'SECRET_MANAGER';
  implemented: boolean;
  selectable: boolean;
  disabledReason?: string;
}

export interface PartnerMetadata {
  authenticationTypes: PartnerAuthTypeMetadata[];
  destinations: PartnerDestinationMetadata[];
  secretProviders: PartnerSecretProviderMetadata[];
}

export type PartnerConnectivityStatus = 'UNKNOWN' | 'PASSED' | 'FAILED' | 'SKIPPED';

export interface PartnerConnectivityValidationRecord extends ResourceRecord {
  partnerConfigId: string;
  organizationId?: string;
  protocol: 'REST' | 'SFTP' | 'WEBHOOK' | 'KAFKA' | 'AS2_GATEWAY';
  status: PartnerConnectivityStatus;
  latencyMs?: number;
  responseMetadata: Record<string, unknown>;
  checkedBy: string;
  checkedAt: string;
}

export type DestinationPolicyStatusCode =
  | 'ALLOWED'
  | 'BLOCKED_BY_ALLOWLIST'
  | 'INVALID_PROTOCOL'
  | 'INVALID_HOST'
  | 'PLACEHOLDER_ENDPOINT'
  | 'POLICY_NOT_APPLICABLE';

export interface DestinationPolicyResult {
  code: string;
  status: DestinationPolicyStatusCode;
  explanation: string;
}

export interface PartnerSecretReferenceRecord {
  name: string;
  provider: 'ENV' | 'VAULT' | 'SECRET_MANAGER';
  reference: string;
}

export interface PartnerRuntimeDestinationRecord {
  code: 'CATOS_PMIS001' | 'PMIS_PMIS002' | 'DUM_API' | 'ADII_AS2';
  endpoint: string;
  authenticationType: PartnerAuthenticationType;
  timeoutMs: number;
  maxAttempts: number;
  backoffMs: number;
  secretRefs: PartnerSecretReferenceRecord[];
  configuration?: Record<string, unknown>;
}

export interface PartnerRuntimeProfileRecord {
  messageFamilies: Array<{ code: string; enabled: boolean }>;
  destinations: PartnerRuntimeDestinationRecord[];
}

export interface PartnerConfigurationVersionRecord {
  partnerConfigId: string;
  version: number;
  lifecycleStatus: NonNullable<PartnerRecord['lifecycleStatus']>;
  snapshot: PartnerRecord;
  actor: string;
  reason?: string;
  createdAt: string;
}

export interface As2TransportProfileRecord {
  id: string;
  integrationPartnerId?: string;
  profileName?: string;
  environmentProfile: 'DEV' | 'TEST' | 'UAT' | 'PROD';
  code: string;
  name: string;
  as2Identifier: string;
  outboundEndpoint: string;
  mdnMode: 'NONE' | 'SYNCHRONOUS' | 'ASYNCHRONOUS';
  active: boolean;
  outboundSigningEnabled: boolean;
  outboundEncryptionEnabled: boolean;
  signedMdnRequired: boolean;
}

export type As2CertificateType = 'LOCAL_SIGNING' | 'PARTNER_SIGNING' | 'PARTNER_ENCRYPTION';

export interface As2CertificateRecord {
  id: string;
  alias: string;
  certificateType: As2CertificateType;
  partnerCode?: string;
  subject?: string;
  issuer?: string;
  serialNumber?: string;
  fingerprintSha256?: string;
  validFrom?: string;
  validUntil?: string;
  active: boolean;
  expired: boolean;
  createdAt: string;
  updatedAt: string;
  deactivatedAt?: string;
}

export type As2CertificateHealthStatus = 'VALID' | 'EXPIRING_SOON' | 'CRITICAL' | 'EXPIRED';

export interface As2CertificateHealthSummary {
  id: string;
  alias: string;
  certificateType: As2CertificateType;
  partnerCode?: string;
  fingerprintSha256?: string;
  validUntil?: string;
  health: As2CertificateHealthStatus;
}

export interface As2CertificateHealthResponse {
  certificates: Record<As2CertificateHealthStatus, As2CertificateHealthSummary[]>;
}

export interface As2CertificateListQuery {
  certificateType?: As2CertificateType | '';
  partnerCode?: string;
  active?: boolean;
  page?: number;
  size?: number;
}

export interface As2CertificateCreateRequest {
  alias: string;
  certificateType: As2CertificateType;
  partnerCode?: string;
  certificatePem: string;
  privateKeyReference?: string;
}

export type As2DlqTaskType = 'OUTBOUND_SEND' | 'INBOUND_ASYNC_MDN_DELIVERY' | 'INBOUND_CALLBACK';
export type As2DlqTaskStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'DEAD_LETTERED' | 'DISCARDED';

export interface As2DlqTaskRecord {
  id: string;
  taskType: As2DlqTaskType;
  referenceId: string;
  partnerCode?: string;
  status: As2DlqTaskStatus;
  retryCount: number;
  maxAttempts: number;
  nextRetryAt?: string;
  lastRetryAt?: string;
  retryCategory?: string;
  lastError?: string;
  completedAt?: string;
  deadLetteredAt?: string;
  deadLetterReason?: string;
  discardedAt?: string;
  discardReason?: string;
}

export interface As2DlqListQuery {
  taskType?: As2DlqTaskType | '';
  partnerCode?: string;
  status?: As2DlqTaskStatus | '';
  fromDate?: string;
  toDate?: string;
  page?: number;
  size?: number;
}

// Transport-level AS2 outbound/inbound message records proxied from the AS2 gateway's
// OutboundMessageResponse/InboundMessageResponse Java DTOs. These carry protocol/security
// metadata only (no payload/body field exists on either DTO) and are distinct from the
// business-level inbound documents resource (As2InboundMessageRecord vs. the existing
// DocumentSummary/DocumentDetails-based inbound documents list) — do not conflate the two.
export type As2MessageStatus =
  | 'REQUEST_RECEIVED'
  | 'PAYLOAD_STORED'
  | 'PAYLOAD_EXTRACTED'
  | 'MESSAGE_CREATED'
  | 'MIC_CALCULATED'
  | 'SEND_ATTEMPTED'
  | 'RECEIVED'
  | 'QUEUED'
  | 'SENDING'
  | 'SENT'
  | 'WAITING_FOR_ASYNC_MDN'
  | 'MDN_OVERDUE'
  | 'RETRY_PENDING'
  | 'RETRYING'
  | 'DEAD_LETTERED'
  | 'MDN_RECEIVED'
  | 'MDN_FAILED'
  | 'AWAITING_MDN'
  | 'DELIVERED'
  | 'FAILED'
  | 'REJECTED';

// Shared nested shapes reused across outbound and inbound message records, since the AS2
// gateway returns identical certificate/MDN/retry/delivery-attempt shapes on both sides.
export interface As2CertificateSummary {
  id: string;
  alias?: string;
  certificateType?: As2CertificateType;
  subject?: string;
  fingerprintSha256?: string;
  validUntil?: string;
}

export interface As2MdnSummary {
  mode?: string;
  disposition?: string;
  receivedContentMic?: string;
  signatureStatus?: string;
  signatureValid?: boolean;
  signingCertificate?: As2CertificateSummary;
  validationStatus?: string;
  failureReason?: string;
  receivedAt?: string;
  /** MDN report body after MIME/multipart-signed envelope stripping -- not the raw wire bytes. */
  rawBody?: string;
}

export interface As2RetrySummary {
  retryTaskId?: string;
  retryState?: string;
  retryCount?: number;
  maxAttempts?: number;
  nextRetryAt?: string;
  lastRetryAt?: string;
  lastRetryReason?: string;
  retryCategory?: string;
  deadLetteredAt?: string;
  deadLetterReason?: string;
}

export interface As2DeliveryAttempt {
  attemptNumber: number;
  status: string;
  httpStatus?: number;
  errorMessage?: string;
  mdnDetected?: boolean;
  mdnDisposition?: string;
  mdnValidationResult?: string;
  mdnSignatureStatus?: string;
  mdnSignatureValid?: boolean;
  startedAt?: string;
  completedAt?: string;
}

export interface As2OutboundMessageRecord {
  messageId: string;
  as2MessageId?: string;
  correlationId?: string;
  partnerCode?: string;
  documentType?: string;
  /** Internal audit label only -- never transmitted to the partner over AS2 (no Content-Disposition on the wire). */
  fileName?: string;
  contentType?: string;
  status: As2MessageStatus;
  micValue?: string;
  micAlgorithm?: string;
  signed: boolean;
  signingAlgorithm?: string;
  signingCertificate?: As2CertificateSummary;
  encrypted: boolean;
  encryptionAlgorithm?: string;
  encryptionCertificate?: As2CertificateSummary;
  securityProcessingStatus?: string;
  mdnMode?: string;
  waitingForAsyncMdn?: boolean;
  asyncMdnReceiveUrl?: string;
  asyncMdnReceivedAt?: string;
  sentAt?: string;
  failureReason?: string;
  mdn?: As2MdnSummary;
  retry?: As2RetrySummary;
  deliveryAttempts: As2DeliveryAttempt[];
}

export interface As2InboundMessageRecord {
  id: string;
  as2MessageId?: string;
  partnerCode?: string;
  /** No filename ever arrives with an inbound AS2 message -- this is the closest meaningful label available. */
  documentType?: string;
  status: As2MessageStatus;
  contentType?: string;
  signed: boolean;
  signatureValid?: boolean;
  encrypted: boolean;
  securityProcessingStatus?: string;
  micValue?: string;
  micAlgorithm?: string;
  receivedAt?: string;
  failureReason?: string;
  callbackStatus?: string;
  mdnMode?: string;
  mdnDeliveryStatus?: string;
  mdnDeliveryUrl?: string;
  mdnSentAt?: string;
  mdnFailureReason?: string;
  mdnSignatureStatus?: string;
  retry?: As2RetrySummary;
  mdn?: As2MdnSummary;
}

export interface As2MessageListQuery {
  partnerCode?: string;
  status?: As2MessageStatus | '';
  correlationId?: string;
  fromDate?: string;
  toDate?: string;
  page?: number;
  size?: number;
  sort?: string;
}

export interface InboundPolicyRecord extends ResourceRecord {
  partnerId: string;
  tenantId?: string;
  flowKey?: string;
  protocol: 'REST' | 'WEBHOOK' | 'KAFKA' | 'SFTP' | 'XML' | 'SOAP';
  authType: 'HMAC' | 'NONE';
  secretRef?: string;
  signatureKeyId?: string;
  algorithm: 'HMAC_SHA256' | 'HMAC_SHA512';
  allowedIps: string[];
  allowedClockSkewSeconds: number;
  replayWindowSeconds: number;
  isActive: boolean;
}

export interface MappingRecord extends ResourceRecord {
  documentType: DocumentType;
  version: string;
  versionNumber?: number;
  definitionId?: string;
  selectedVersionId?: string;
  currentVersionId?: string;
  activeVersionId?: string;
  lifecycleState?: GovernanceLifecycleState;
  isActive: boolean;
  sourceSchemaRef?: string;
  targetFormat: 'EDI' | 'XML';
  ruleDefinition: Record<string, unknown>;
}

export type GovernanceLifecycleState = 'DRAFT' | 'VALIDATED' | 'APPROVED' | 'ACTIVE';

export interface MappingVersionRecord extends ResourceRecord {
  definitionId: string;
  versionNumber: number;
  versionLabel: string;
  lifecycleState: GovernanceLifecycleState;
  sourceSchemaRef?: string;
  targetFormat: 'EDI' | 'XML';
  ruleDefinition: Record<string, unknown>;
  validatedAt?: string;
  approvedAt?: string;
  activatedAt?: string;
}

export interface TemplateRecord extends ResourceRecord {
  documentType: DocumentType;
  versionLabel?: string;
  versionNumber?: number;
  definitionId?: string;
  selectedVersionId?: string;
  currentVersionId?: string;
  activeVersionId?: string;
  lifecycleState?: GovernanceLifecycleState;
  description?: string;
  partnerConfigId?: string;
  templateBody?: string;
  defaultSourcePayload: Record<string, unknown>;
  isActive: boolean;
}

export interface ValidationRuleRecord extends ResourceRecord {
  documentType: DocumentType;
  versionLabel?: string;
  versionNumber?: number;
  definitionId?: string;
  selectedVersionId?: string;
  currentVersionId?: string;
  activeVersionId?: string;
  lifecycleState?: GovernanceLifecycleState;
  severity: 'error' | 'warning';
  ruleType: 'structure' | 'semantic';
  ruleDefinition: Record<string, unknown>;
  isActive: boolean;
}

export interface TemplateVersionRecord extends ResourceRecord {
  definitionId: string;
  versionNumber: number;
  versionLabel: string;
  lifecycleState: GovernanceLifecycleState;
  description?: string;
  templateBody?: string;
  defaultSourcePayload: Record<string, unknown>;
  validatedAt?: string;
  approvedAt?: string;
  activatedAt?: string;
}

export interface ValidationRuleVersionRecord extends ResourceRecord {
  definitionId: string;
  versionNumber: number;
  versionLabel: string;
  lifecycleState: GovernanceLifecycleState;
  severity: 'error' | 'warning';
  ruleType: 'structure' | 'semantic';
  ruleDefinition: Record<string, unknown>;
  validatedAt?: string;
  approvedAt?: string;
  activatedAt?: string;
}

export type ConnectorProtocolFamily = 'REST' | 'WEBHOOK' | 'SFTP' | 'KAFKA' | 'SOAP' | 'MANUAL';
export type ConnectorCapabilityType = 'OUTBOUND_DELIVERY';
export type ConnectorHealthStatus = 'UNKNOWN' | 'HEALTHY' | 'DEGRADED' | 'UNHEALTHY' | 'DISABLED';

export interface ConnectorDefinitionRecord extends ResourceRecord {
  code: string;
  protocolFamily: ConnectorProtocolFamily;
  capabilityType: ConnectorCapabilityType;
  description?: string;
  driverRef: string;
  supportedAuthSchemes: string[];
  isActive: boolean;
}

export interface ConnectorInstanceRecord extends ResourceRecord {
  definitionId: string;
  protocolFamily: ConnectorProtocolFamily;
  capabilityType: ConnectorCapabilityType;
  partnerConfigId?: string;
  endpoint?: string;
  ownership: Record<string, unknown>;
  timeoutPolicy: Record<string, unknown>;
  retryPolicy: Record<string, unknown>;
  health: {
    status: ConnectorHealthStatus;
    checkedAt?: string;
    message?: string;
  };
  config: Record<string, unknown>;
  secretRefs: Array<Record<string, unknown>>;
  isActive: boolean;
}

export type ExchangeStatus =
  | 'RECEIVED'
  | 'IN_PROGRESS'
  | 'SUCCEEDED'
  | 'FAILED'
  | 'WAITING_RETRY'
  | 'DEAD_LETTERED'
  | 'ARCHIVED';

export type ExchangeKind =
  | 'DOCUMENT_GENERATE'
  | 'DOCUMENT_VALIDATE'
  | 'DOCUMENT_RESEND'
  | 'DOCUMENT_REBUILD';

export interface ExchangeListQuery {
  page?: number;
  pageSize?: number;
  status?: ExchangeStatus | '';
  exchangeKind?: ExchangeKind | '';
  documentId?: string;
  documentType?: string;
  correlationId?: string;
  traceId?: string;
  referenceId?: string;
}

export interface ExchangeStepRecord {
  id: string;
  stepName: string;
  sequenceNumber: number;
  status: 'SUCCEEDED' | 'FAILED';
  details: Record<string, unknown>;
  traceId?: string;
  startedAt: string;
  finishedAt?: string;
  createdAt: string;
}

export interface RetryAttemptRecord {
  id: string;
  status: 'REQUESTED';
  reason?: string;
  requestedBy?: string;
  traceId?: string;
  requestedAt: string;
}

export interface DeadLetterEntryRecord {
  id: string;
  reason: string;
  actor?: string;
  traceId?: string;
  quarantineMode?: boolean;
  operatorComment?: string;
  createdAt: string;
}

export interface ReplayRequestRecord {
  id: string;
  status: 'REQUESTED';
  reason?: string;
  requestedBy?: string;
  traceId?: string;
  createdAt: string;
}

export interface ExchangeOutboxEventRecord {
  id: string;
  eventType: 'exchange.retry.requested' | 'exchange.dead-lettered' | 'exchange.replay.requested';
  status: 'PENDING' | 'IN_PROGRESS' | 'PUBLISHED' | 'FAILED' | 'DEAD_LETTERED';
  payload: Record<string, unknown>;
  availableAt: string;
  createdAt: string;
  attemptCount?: number;
  maxAttempts?: number;
  workerId?: string;
  lastError?: string;
}

export interface ExchangeExecutionFailureRecord {
  classification: string;
  code: string;
  message: string;
  retryable: boolean;
  details?: Record<string, unknown>;
}

export interface ExchangeExecutionResultRecord {
  handlerId: string;
  mode: 'local-bridge' | 'connector-runtime' | 'flow-runtime';
  target: 'document' | 'connector' | 'flow' | 'notification';
  action: string;
  summary: string;
  details: Record<string, unknown>;
  failure?: ExchangeExecutionFailureRecord;
}

export interface ExchangeExecutionRequestRecord {
  id: string;
  sourceEventId: string;
  executionType: 'RETRY_EXCHANGE' | 'REPLAY_EXCHANGE' | 'DEAD_LETTER_NOTIFICATION';
  status: 'READY' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'DEAD_LETTERED';
  payload: Record<string, unknown>;
  createdAt: string;
  completedAt?: string;
  nextAttemptAt?: string;
  attemptCount?: number;
  maxAttempts?: number;
  workerId?: string;
  idempotencyKey?: string;
  errorMessage?: string;
  failureClassification?: string;
  failureCode?: string;
  result?: ExchangeExecutionResultRecord;
}

export interface ExchangeSummary {
  id: string;
  exchangeKind: ExchangeKind;
  documentId?: string;
  documentType: string;
  status: ExchangeStatus;
  partnerId?: string;
  referenceId?: string;
  correlationId?: string;
  actor?: string;
  traceId?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

export interface ExchangeDetails extends ExchangeSummary {
  steps: ExchangeStepRecord[];
  retryAttempts: RetryAttemptRecord[];
  deadLetterEntry?: DeadLetterEntryRecord;
  replayRequests: ReplayRequestRecord[];
  outboxEvents: ExchangeOutboxEventRecord[];
  executionRequests: ExchangeExecutionRequestRecord[];
}

export type FlowLifecycleState = 'DRAFT' | 'APPROVED' | 'ACTIVE' | 'RETIRED';
export type FlowExecutionStatus = 'RECEIVED' | 'IN_PROGRESS' | 'SUCCEEDED' | 'FAILED' | 'DEAD_LETTERED';

export interface FlowStepDefinitionRecord {
  id: string;
  name: string;
  type:
    | 'INBOUND_CHANNEL'
    | 'PARSER'
    | 'VALIDATOR'
    | 'TRANSFORMER'
    | 'ROUTER'
    | 'OUTBOUND_CONNECTOR'
    | 'PARALLEL_BRANCH'
    | 'ERROR_BRANCH'
    | 'COMPENSATION';
  sequence: number;
  condition?: string;
  config: Record<string, unknown>;
  onErrorStepId?: string;
  compensationStepId?: string;
}

export interface FlowVersionRecord {
  id: string;
  flowId: string;
  versionNumber: number;
  versionLabel: string;
  lifecycleState: FlowLifecycleState;
  inboundChannel?: Record<string, unknown>;
  parser?: Record<string, unknown>;
  validator?: Record<string, unknown>;
  transformer?: Record<string, unknown>;
  router?: Record<string, unknown>;
  outboundConnectorId?: string;
  mappingDefinitionId?: string;
  connectorDefinitionId?: string;
  mappingVersionId?: string;
  connectorInstanceId?: string;
  connectorVersion?: string;
  destinationPartnerId?: string;
  autoDeliver: boolean;
  retryPolicy: Record<string, unknown>;
  dlqPolicy: Record<string, unknown>;
  steps: FlowStepDefinitionRecord[];
  createdAt: string;
  updatedAt: string;
  approvedAt?: string;
  activatedAt?: string;
}

export interface FlowRecord extends ResourceRecord {
  code: string;
  description?: string;
  organizationId?: string;
  currentVersionId: string;
  activeVersionId?: string;
  selectedVersion: FlowVersionRecord;
}

export interface FlowStepExecutionRecord {
  id: string;
  executionId: string;
  stepId: string;
  stepName: string;
  stepType: FlowStepDefinitionRecord['type'];
  sequence: number;
  status: 'SKIPPED' | 'SUCCEEDED' | 'FAILED';
  startedAt: string;
  finishedAt?: string;
  inputSnapshot: Record<string, unknown>;
  outputSnapshot: Record<string, unknown>;
  error?: string;
}

export interface FlowExecutionRecord {
  id: string;
  flowId: string;
  flowVersionId: string;
  flowVersionNumber: number;
  mappingVersionId?: string;
  connectorVersion?: string;
  connectorInstanceId?: string;
  status: FlowExecutionStatus;
  correlationId?: string;
  referenceId?: string;
  partnerId?: string;
  actor?: string;
  executionPath: string[];
  retryHistory: Record<string, unknown>[];
  payloadSnapshot: Record<string, unknown>;
  resultSnapshot: Record<string, unknown>;
  error?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  steps: FlowStepExecutionRecord[];
}

export type EaiSecurityScheme =
  | 'API_KEY'
  | 'OAUTH2_CLIENT'
  | 'HMAC_SIGNATURE'
  | 'MTLS_CERTIFICATE'
  | 'IP_ALLOWLIST'
  | 'SFTP_CREDENTIALS'
  | 'KAFKA_TOPIC_ACL';

export type MappingRuleType =
  | 'direct'
  | 'constant'
  | 'dateFormat'
  | 'enumTranslation'
  | 'concat'
  | 'conditional'
  | 'array';

export interface SchemaFieldNode {
  path: string;
  name: string;
  type: 'object' | 'array' | 'string' | 'number' | 'boolean' | 'null' | 'unknown';
  required: boolean;
  children?: SchemaFieldNode[];
  example?: unknown;
}

export interface MessageSchemaDraft {
  id: string;
  name: string;
  messageType: DocumentType;
  version: string;
  format: 'JSON' | 'XML';
  status: 'DRAFT' | 'VALIDATED' | 'PUBLISHED';
  fields: SchemaFieldNode[];
  samplePayload: string;
  createdAt: string;
}

export interface MappingRuleDraft {
  id: string;
  type: MappingRuleType;
  sourcePath: string;
  targetPath: string;
  expression: string;
  description?: string;
}

export interface FunctionalTestResult {
  validation: { status: 'PASSED' | 'FAILED'; errors: string[]; warnings: string[] };
  canonicalEnvelope: Record<string, unknown>;
  canonicalPayload: Record<string, unknown>;
  targetPayload: Record<string, unknown>;
  delivery: { status: 'SIMULATED' | 'BLOCKED'; target: string; message: string };
  trace?: Array<{ step: string; status: string; detail: string; durationMs: number }>;
  timing?: { totalMs: number };
  persistedReferenceId?: string;
}

export interface FlowDryRunResult {
  transformedPayload: Record<string, unknown>;
  validationErrors?: string[];
}

export type SchemaLifecycleState = 'DRAFT' | 'VALIDATED' | 'APPROVED' | 'ACTIVE' | 'DEPRECATED' | 'ARCHIVED';
export type SchemaFormat = 'JSON_SCHEMA' | 'XML_SCHEMA' | 'EDIFACT';
export type SchemaCompatibilityMode = 'BACKWARD' | 'FORWARD' | 'FULL' | 'NONE';

export interface RegisteredSchemaRecord {
  id: string;
  name: string;
  version: string;
  format: SchemaFormat;
  lifecycleState: SchemaLifecycleState;
  compatibilityMode: SchemaCompatibilityMode;
  ownerTeam: string;
  domain: string;
  entityType: string;
  contentType: string;
  schemaDefinition: Record<string, unknown> | string;
  metadata: Record<string, unknown>;
  createdBy?: string;
  approvedBy?: string;
  activatedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface StorePayloadRequest {
  schemaId?: string;
  schemaName?: string;
  schemaVersion?: string;
  contentType: string;
  retentionClass: 'HOT' | 'WARM' | 'ARCHIVE' | 'COMPLIANCE';
  payload: Record<string, unknown> | string;
  encrypted?: boolean;
  storageUri?: string;
}

export interface StoredPayloadReference {
  id: string;
  schemaId?: string;
  schemaName?: string;
  schemaVersion?: string;
  contentType: string;
  retentionClass: string;
  sha256: string;
  byteSize: number;
  encrypted: boolean;
  storageUri?: string;
  payloadJson?: Record<string, unknown>;
  payloadText?: string;
  createdBy?: string;
  createdAt: string;
}

export type MaritimeEdifactStandard =
  | 'COPARN'
  | 'COARRI'
  | 'CODECO'
  | 'CUSCAR'
  | 'CUSRES'
  | 'IFTSTA'
  | 'IFTMAN'
  | 'COREOR';

export type MaritimeSystemTemplateType = 'PMIS' | 'PCS' | 'TOS' | 'CUSTOMS';

export interface EdifactSegmentRecord {
  tag: string;
  elements: string[][];
  raw: string;
  position: number;
}

export interface MaritimeValidationIssueRecord {
  stage: 'syntax' | 'semantic';
  severity: 'error' | 'warning';
  code: string;
  message: string;
  segment?: string;
}

export interface PartnerMappingDefinitionRecord {
  id: string;
  partnerCode: string;
  standard: MaritimeEdifactStandard;
  name: string;
  inheritsFrom?: string;
  overrides: Record<string, string>;
  effectiveMapping: Record<string, string>;
}

export interface MaritimeExchangeRecord {
  id: string;
  standard: MaritimeEdifactStandard;
  partnerCode: string;
  systemType: MaritimeSystemTemplateType;
  stages: string[];
  parseResult: {
    standard: MaritimeEdifactStandard;
    syntaxIdentifier?: string;
    interchangeReference?: string;
    messageReference?: string;
    segments: EdifactSegmentRecord[];
  };
  syntaxIssues: MaritimeValidationIssueRecord[];
  semanticIssues: MaritimeValidationIssueRecord[];
  acknowledgement: {
    status: 'ACK' | 'NACK';
    functionalCode: '7' | '4';
    generatedAt: string;
    body: string;
    issues: MaritimeValidationIssueRecord[];
  };
  transformedPayload: Record<string, unknown>;
  audit: {
    vessel?: string;
    voyage?: string;
    escale?: string;
    partner?: string;
    terminal?: string;
    receivedAt: string;
    processedAt: string;
  };
  delivery: {
    status: 'PENDING' | 'DELIVERED' | 'FAILED';
    attempts: number;
    lastAttemptAt?: string;
  };
  lastReplay?: {
    reason: string;
    requestedAt: string;
  };
  createdAt: string;
}

export interface MaritimeDashboardRecord {
  supportedStandards: MaritimeEdifactStandard[];
  totalExchanges: number;
  ackCount: number;
  nackCount: number;
  deliveryPending: number;
  highVolume: {
    batchSize: number;
    replayWindowHours: number;
    maxMessagesPerBatch: number;
  };
  templates: Array<{
    id: string;
    type: MaritimeSystemTemplateType;
    name: string;
    description: string;
    supportedStandards: MaritimeEdifactStandard[];
    connectorProfiles: Array<{ protocol: string; purpose: string }>;
    defaultMappingId: string;
    replay: { batchSize: number; idempotencyKeyFields: string[] };
  }>;
  recentExchanges: MaritimeExchangeRecord[];
}

export interface ProcessMaritimeMessageRequest {
  standard: MaritimeEdifactStandard;
  partnerCode: string;
  systemType?: MaritimeSystemTemplateType;
  message: string;
  mappingId?: string;
  overrides?: Record<string, string>;
  audit?: {
    vessel?: string;
    voyage?: string;
    escale?: string;
    partner?: string;
    terminal?: string;
  };
}
